import { ChevronLeft, ChevronRight, Event } from "@mui/icons-material";
import { alpha, IconButton, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import type { Dayjs } from "dayjs";
import { useState } from "react";
import "dayjs/locale/pt-br";

export interface FitnessModalDateRowProps {
  selectedDate: Dayjs;
  /** Último dia permitido (ex.: hoje). */
  today: Dayjs;
  loading: boolean;
  onPrevDay: () => void;
  onNextDay: () => void;
  /** Data escolhida no calendário (o pai aplica limite até `today`). */
  onPickDate: (date: Dayjs) => void;
  /** Ocupa 100% da largura do pai (ex.: linha com botão de busca ao lado). */
  fullWidth?: boolean;
  /**
   * Modo compacto: setas e campo de data menores (ex.: painel estreito no atendimento).
   */
  compact?: boolean;
  /**
   * Largura só do conteúdo, alinhado ao início (ex.: faixa verde não ocupa a tela toda).
   */
  inline?: boolean;
}

/**
 * Faixa de data do modal fitness: setas + texto que abre o DatePicker (mesmo padrão do agendamento).
 */
export function FitnessModalDateRow({
  selectedDate,
  today,
  loading,
  onPrevDay,
  onNextDay,
  onPickDate,
  fullWidth = false,
  compact = false,
  inline = false,
}: FitnessModalDateRowProps) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  return (
    <Stack
      direction="row"
      spacing={compact ? 0.65 : 1}
      alignItems="center"
      justifyContent={inline ? "flex-start" : "center"}
      sx={{
        width: fullWidth
          ? "100%"
          : inline
            ? "auto"
            : { xs: "100%", sm: "auto" },
        minWidth: 0,
      }}
    >
      <IconButton
        size="small"
        disabled={loading}
        onClick={onPrevDay}
        aria-label="Dia anterior"
        sx={{
          color: "white",
          flexShrink: 0,
          p: compact ? 0.5 : undefined,
          bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.12),
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.2),
          },
          "&.Mui-disabled": {
            color: (theme) => alpha(theme.palette.neutral0.main, 0.3),
            bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.06),
          },
        }}
      >
        <ChevronLeft sx={{ fontSize: compact ? 22 : 24 }} />
      </IconButton>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
        <DatePicker
          open={datePickerOpen}
          onClose={() => setDatePickerOpen(false)}
          value={selectedDate}
          maxDate={today}
          onChange={(newDate) => {
            if (newDate) {
              onPickDate(newDate);
              setDatePickerOpen(false);
            }
          }}
          format={
            compact ? "DD/MM/YYYY" : "DD [de] MMMM [de] YYYY"
          }
          sx={{
            flex: compact ? "0 1 auto" : "1 1 auto",
            minWidth: 0,
            maxWidth: compact ? 228 : undefined,
            "& .MuiInputBase-root": {
              color: "white",
              bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.12),
              borderRadius: 2,
              minHeight: compact ? 38 : undefined,
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.2),
              },
              "& fieldset": {
                border: "none",
              },
            },
            "& .MuiInputBase-input": {
              textAlign: "center",
              fontWeight: 600,
              py: compact ? 0.5 : 1,
              px: compact ? 0.5 : { xs: 1, sm: 2 },
              fontSize: compact
                ? "0.8125rem"
                : { xs: "0.8rem", sm: "1rem" },
              lineHeight: 1.35,
              cursor: "pointer",
            },
            "& .MuiSvgIcon-root": {
              color: "white",
            },
          }}
          slotProps={{
            textField: {
              variant: "outlined",
              InputProps: {
                startAdornment: (
                  <Event
                    sx={{
                      color: "white",
                      mr: compact ? 0.35 : 0.5,
                      fontSize: 18,
                    }}
                  />
                ),
                endAdornment: <></>,
              },
              onClick: () => setDatePickerOpen(true),
            },
            popper: {
              sx: {
                "& .MuiPaper-root": {
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.12)",
                  borderRadius: 2,
                },
              },
            },
          }}
        />
      </LocalizationProvider>

      <IconButton
        size="small"
        disabled={loading || selectedDate.isSame(today, "day")}
        onClick={onNextDay}
        aria-label="Próximo dia"
        sx={{
          color: "white",
          flexShrink: 0,
          p: compact ? 0.5 : undefined,
          bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.12),
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.2),
          },
          "&.Mui-disabled": {
            color: (theme) => alpha(theme.palette.neutral0.main, 0.3),
            bgcolor: (theme) => alpha(theme.palette.neutral0.main, 0.06),
          },
        }}
      >
        <ChevronRight sx={{ fontSize: compact ? 22 : 24 }} />
      </IconButton>
    </Stack>
  );
}
